import { makeAuthenticatedRequest } from './authService';

export interface GoldPrice {
  id: number;
  date: string;
  region: string;
  gold_type: string;
  buy_price: number;
  sell_price: number;
  update_time: string;
  created_at: string;
  updated_at: string;
}

export interface GoldPriceFilters {
  region?: string;
  gold_type?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

export interface GoldPriceStats {
  min_buy_price: number;
  max_buy_price: number;
  avg_buy_price: number;
  min_sell_price: number;
  max_sell_price: number;
  avg_sell_price: number;
  total_records: number;
  earliest_date: string | null;
  latest_date: string | null;
}

export interface GoldPricesResponse {
  success: boolean;
  prices?: GoldPrice[];
  count?: number;
  total?: number;
  limit?: number;
  offset?: number;
}

class GoldPricesService {
  /**
   * Get gold prices with optional filters
   */
  async getGoldPrices(filters?: GoldPriceFilters): Promise<GoldPricesResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters?.region) queryParams.append('region', filters.region);
      if (filters?.gold_type) queryParams.append('gold_type', filters.gold_type);
      if (filters?.start_date) queryParams.append('start_date', filters.start_date);
      if (filters?.end_date) queryParams.append('end_date', filters.end_date);
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/gold-prices${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching gold prices:', error);
      throw error;
    }
  }

  /**
   * Get distinct regions
   */
  async getRegions(): Promise<{ success: boolean; regions?: string[]; count?: number }> {
    try {
      const response = await makeAuthenticatedRequest('/gold-prices/regions', {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching regions:', error);
      throw error;
    }
  }

  /**
   * Get distinct gold types
   */
  async getGoldTypes(): Promise<{ success: boolean; types?: string[]; count?: number }> {
    try {
      const response = await makeAuthenticatedRequest('/gold-prices/types', {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching gold types:', error);
      throw error;
    }
  }

  /**
   * Get price statistics
   */
  async getStats(filters?: Omit<GoldPriceFilters, 'limit' | 'offset'>): Promise<{ success: boolean; stats?: GoldPriceStats }> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters?.region) queryParams.append('region', filters.region);
      if (filters?.gold_type) queryParams.append('gold_type', filters.gold_type);
      if (filters?.start_date) queryParams.append('start_date', filters.start_date);
      if (filters?.end_date) queryParams.append('end_date', filters.end_date);
      
      const queryString = queryParams.toString();
      const url = `/gold-prices/stats${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }

  /**
   * Extract missing gold prices for the past 30 days
   */
  async extractMissingPrices(): Promise<{ 
    success: boolean; 
    message?: string;
    extracted?: number; 
    failed?: number; 
    missing_dates?: string[];
    failed_dates?: string[];
  }> {
    try {
      const response = await makeAuthenticatedRequest('/gold-prices/extract-missing', {
        method: 'POST'
      });
      return response;
    } catch (error) {
      console.error('Error extracting missing prices:', error);
      throw error;
    }
  }

  /**
   * Format currency as VND
   */
  formatCurrency(amount: number): string {
    if (amount === null || amount === undefined || isNaN(amount) || !isFinite(amount)) {
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(0);
    }

    const validAmount = typeof amount === 'number' ? amount : parseFloat(String(amount));
    
    if (isNaN(validAmount) || !isFinite(validAmount)) {
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(0);
    }

    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(validAmount);
  }

  /**
   * Format date consistently
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  /**
   * Format datetime consistently
   */
  formatDateTime(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}

export const goldPricesService = new GoldPricesService();

